import { instanceBo } from "../utils/axios.create";
import {
	CupDependencies,
	CupResponse,
	CupsResponse,
	CupStatus,
} from "@/domain/cup.interface";

export interface VendorInUse {
	vendor: string;
	cupCount: number;
}

export interface VendorInUseResponse {
	statusCode: number;
	body: VendorInUse[];
}

export interface CupRequest {
	accountId: string;
	name: string;
	vendor: string;
	display: string;
	description: string;
	status: CupStatus;
	analytes?: string[];
	createdBy: string;
}

export interface CupUpdateRequest extends Omit<CupRequest, "createdBy"> {
	_id: string;
	updatedBy: string;
}

export interface CupSearchParams {
	search?: string;
	name?: string;
	vendor?: string;
	accountId?: string;
}

const getCups = async (): Promise<CupsResponse> => {
	return instanceBo
		.get("/cups")
		.then((res) => res.data)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const getCupById = async (id: string): Promise<CupResponse> => {
	return instanceBo
		.get(`/cups/${id}`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const searchCups = async (params: CupSearchParams): Promise<CupsResponse> => {
	const queryParams = new URLSearchParams();

	if (params.search) queryParams.append("search", params.search);
	if (params.name) queryParams.append("name", params.name);
	if (params.vendor) queryParams.append("vendor", params.vendor);
	if (params.accountId) queryParams.append("accountId", params.accountId);

	const queryString = queryParams.toString();
	const url = `/cups${queryString ? `?${queryString}` : ""}`;

	return instanceBo
		.get(url)
		.then((res) => res.data)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const createCup = async (data: CupRequest): Promise<CupResponse> => {
	return instanceBo
		.post("/cups", data)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const updateCup = async (data: CupUpdateRequest): Promise<CupResponse> => {
	const { _id, ...updateData } = data;
	return instanceBo
		.put(`/cups/${_id}`, updateData)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const deleteCup = async (id: string): Promise<void> => {
	return instanceBo
		.delete(`/cups/${id}`)
		.then((res) => res.data)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

/**
 * SIMS-8850 AC1-AC4 — the counts the delete confirmation needs.
 *
 * Errors are REJECTED rather than defaulted to zero, following the payer precedent: "no
 * dependencies" on a failed request is exactly how a cup with hundreds of recorded visits would
 * be offered for deletion, and zero is the signal the administrator reads as "safe".
 */
const getDependencies = async (id: string): Promise<CupDependencies> => {
	return instanceBo
		.get(`/cups/${id}/dependencies`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

/**
 * SIMS-8799 AC2 / SIMS-8962 AC2 — the vendors already in use, each with the number of cups using
 * it, so the editor can suggest an existing spelling instead of letting a near-duplicate become
 * its own filter entry on the list.
 *
 * The endpoint shipped with SIMS-8962 and had no consumer until now.
 */
const getInUseVendors = async (): Promise<VendorInUseResponse> => {
	return instanceBo
		.get("/cups/vendors")
		.then((res) => res.data)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

export const cupService = {
	getCups,
	getDependencies,
	getInUseVendors,
	getCupById,
	searchCups,
	createCup,
	updateCup,
	deleteCup,
};
